import type { BlockedIndicator, LegalForm } from '@gallop/domain'
import type { Role, TeamInvite, TeamSeat, WorkItem } from './types'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api } from './client'
import { Bootstrap, SearchResult } from './types'

export const keys = {
  bootstrap: ['workspace', 'bootstrap'] as const,
  search: (q: string) => ['workspace', 'search', q] as const,
  myWork: ['work-items', 'me'] as const,
  stalledWork: ['work-items', 'stalled'] as const,
  blockedWork: ['work-items', 'blocked'] as const,
  fileItems: (fileId: string) => ['work-items', 'file', fileId] as const,
  fileAttachments: (fileId: string, period: string, itemId: string | null) => ['attachments', 'file', fileId, period, itemId] as const,
  clusterItems: (clusterId: string) => ['work-items', 'cluster', clusterId] as const,
  client: (id: string) => ['clients', id] as const,
  file: (id: string) => ['files', id] as const,
  team: ['team'] as const,
  teamWork: (period: string) => ['team', 'work', period] as const,
  checklist: (fileId: string, period: string) => ['checklist', fileId, period] as const,
  groupClose: (clusterId: string, period: string) => ['group-close', clusterId, period] as const,
  groupCloseAll: (period: string) => ['group-close', 'all', period] as const,
  recurring: (fileId: string) => ['recurring', fileId] as const,
  template: (clusterId: string) => ['template', clusterId] as const,
  firmTemplates: ['template', 'firm'] as const,
  firmTemplate: (lineageId: string) => ['template', 'firm', lineageId] as const,
  item: (id: string) => ['work-items', 'one', id] as const,
  itemThread: (id: string) => ['work-items', 'thread', id] as const,
  notifications: ['notifications'] as const,
  // PRD-025 slice 2b
  threads: (filters: ThreadFilters) => ['threads', 'list', filters.period ?? 'all', filters.state ?? 'all', filters.fileId ?? '', filters.clientId ?? '', filters.waiting ?? ''] as const,
  thread: (id: string) => ['threads', 'one', id] as const,
  agentWriteTypes: (fileId: string) => ['files', fileId, 'agent', 'write-types'] as const,
  fileSkills: (fileId: string) => ['files', fileId, 'skills'] as const,
  skills: ['skills'] as const,
}

export function useBootstrap() {
  return useQuery({
    queryKey: keys.bootstrap,
    queryFn: async () => Bootstrap.parse(await api('/workspace/bootstrap')),
    staleTime: 60_000,
    refetchInterval: 15 * 60_000,
    // PRD-026 v0.3 (6): the operator flag and other permission changes land on the next focus, not on a hard refresh (ETag makes the re-read cheap).
    refetchOnWindowFocus: 'always',
    retry: (count, err) => !(err instanceof Error && err.name === 'UnauthenticatedError') && count < 2,
  })
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: async () => SearchResult.array().parse((await api<{ results: unknown[] }>(`/workspace/search?q=${encodeURIComponent(q)}`)).results),
    enabled: q.trim().length > 0,
    staleTime: 10_000,
  })
}

export function useMyWork() {
  return useQuery({ queryKey: keys.myWork, queryFn: async () => (await api<{ workItems: WorkItem[] }>('/work-items?assignee=me')).workItems })
}

/**
 * PRD-018 v0.4 §3.18 / PRD-017 §6.5 v0.19: the firm-wide Work list. One cursor-paged `GET /work-items` with the
 * filters in the URL and `totals=true` for the facts line; RLS trims the rows to what the viewer may read. `period`
 * keeps close rows on the header month; items with no period show regardless (the API tags them).
 */
export interface WorkListFilters {
  client?: string | null
  assignee?: string | null
  status?: string[] | null
  kind?: string | null
  workClass?: string | null
  overdue?: boolean
  mine?: boolean
  unfiled?: boolean
  period?: string | null
  /** Default true: the list is "All open" unless a status filter names done. */
  open?: boolean
}
export interface WorkListPage { workItems: WorkItem[], nextCursor: string | null, totals?: { open: number, waiting: number, overdue: number, doneThisPeriod: number } }
export const WORK_LIST_PAGE = 100
export function workListQuery(f: WorkListFilters): URLSearchParams {
  const qs = new URLSearchParams()
  if (f.client)
    qs.set('client', f.client)
  if (f.assignee)
    qs.set('assignee', f.assignee)
  if (f.status && f.status.length > 0)
    qs.set('status', f.status.join(','))
  else if (f.open !== false)
    qs.set('open', 'true')
  if (f.kind)
    qs.set('kind', f.kind)
  if (f.workClass)
    qs.set('workClass', f.workClass)
  if (f.overdue)
    qs.set('overdue', 'true')
  if (f.mine)
    qs.set('mine', 'true')
  if (f.unfiled)
    qs.set('unfiled', 'true')
  if (f.period)
    qs.set('period', f.period)
  qs.set('totals', 'true')
  qs.set('limit', String(WORK_LIST_PAGE))
  return qs
}
export function useWorkList(f: WorkListFilters, opts: { enabled?: boolean } = {}) {
  const base = workListQuery(f)
  return useInfiniteQuery({
    enabled: opts.enabled ?? true,
    queryKey: ['work-items', 'firm', base.toString()],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams(base)
      if (pageParam)
        qs.set('cursor', pageParam)
      return api<WorkListPage>(`/work-items?${qs}`)
    },
    getNextPageParam: last => last.nextCursor ?? undefined,
  })
}
/** Today exceptions: scope comes from the signed-in principal, not the assignee filter or header month. */
export function useStalledWork() {
  return useQuery({ queryKey: keys.stalledWork, queryFn: async () => (await api<{ workItems: WorkItem[] }>('/work-items?stalled=true')).workItems, refetchInterval: 60_000 })
}
/**
 * PRD-018 §3.5 Home: every blocked item the person may read, in one request, so the Clients grid can show a
 * blocked and stalled count per card without a fetch per client (50 clients would otherwise mean 50 calls).
 * RLS scopes the rows to the person (PRD-019); the `blocked` projection carries `stalled` (PRD-017 v0.12).
 */
export function useBlockedWork() {
  return useQuery({ queryKey: keys.blockedWork, queryFn: async () => (await api<{ workItems: WorkItem[] }>('/work-items?status=blocked')).workItems, refetchInterval: 60_000 })
}
export function useFileItems(fileId: string) {
  return useQuery({ queryKey: keys.fileItems(fileId), queryFn: async () => (await api<{ workItems: WorkItem[] }>(`/work-items?scope=file:${fileId}`)).workItems })
}
export function useClusterItems(clusterId: string) {
  return useQuery({ queryKey: keys.clusterItems(clusterId), queryFn: async () => (await api<{ workItems: WorkItem[] }>(`/work-items?scope=cluster:${clusterId}`)).workItems })
}

/** PRD-020 Q10 amended 2026-09-13: one place a client or file is taxed or licensed in; rows shared per account. */
export interface Jurisdiction { id: string, country: string, state: string, county: string | null, city: string | null }
/** QA-4: a postal address as the API answers it (company data; not in the never-send class). */
export interface Address { line1: string | null, line2: string | null, city: string | null, state: string | null, postalCode: string | null, country: string | null }
/** The client's address as shown: its own, or on a single-entity client its file's, labeled by source. */
export interface EffectiveAddress { address: Address | null, source: 'client' | 'file' | null, fileId: string | null, fileName?: string }
export type ConsolidationBasis = 'consolidated' | 'combined'
export type FileBooks = 'qbo' | 'none'
export interface ClientDetail { id: string, name: string, structure: 'single' | 'group', status: 'active' | 'prospect' | 'archived', close_target_day: number | null, effective_close_target_day: number, fiscal_year_end_month: number, lead_seat_id: string | null, note: string | null, updated_at: string, address: Address | null, effective_address: EffectiveAddress, jurisdictions: Jurisdiction[], /** QA-28 / ADR 0028: how the group's statements are put together; null until the group is asked. */ consolidation_basis: ConsolidationBasis | null, top_file_id: string | null, /** PRD-020 §3.14: set while archived; the actor comes from the archive event. */ archived_at: string | null, archived_by: string | null }
export interface ClientFileRow { id: string, display_name: string, legal_name: string | null, entity_type: string | null, role: FileRole, realm_id: string | null, connection_state: ConnectionState, books: FileBooks, last_sync_at: string | null, last_sync_status: string | null, last_sync_reason: string | null, manager_seat_id: string | null }
/** PRD-020 §3.14: a file under "Archived (n)" on the client page. */
export interface ArchivedFileRow { id: string, display_name: string, legal_name: string | null, entity_type: string | null, role: FileRole, realm_id: string | null, books: FileBooks, archived_at: string, archived_by: string | null }
export interface FileDetail { id: string, client_id: string, display_name: string, legal_name: string | null, entity_type: string | null, /** PRD-028 §3.1: what the entity is under state law; edited on the Relationships tab only. Null until a person sets it. */ legal_form: LegalForm | null, role: FileRole, fiscal_year_start_month: number, realm_id: string | null, connection_state: ConnectionState, /** QA-27: `none` = paper entity. */ books: FileBooks, last_sync_at: string | null, last_sync_status: string | null, last_sync_reason: string | null, manager_seat_id: string | null, lead_seat_id: string | null, updated_at: string, address: Address | null, qbo_address: Address | null, address_review_pending: boolean, /** Edge case 12: what QuickBooks last reported (1–12) and whether it differs from the file's setting without having been kept. */ qbo_fiscal_year_start_month: number | null, fiscal_year_review_pending: boolean, jurisdictions: Jurisdiction[], client_jurisdictions: Jurisdiction[], /** PRD-020 §3.14. */ archived_at: string | null, archived_by: string | null }
export function useClient(id: string) {
  return useQuery({ queryKey: keys.client(id), queryFn: () => api<{ client: ClientDetail, files: ClientFileRow[], archivedFiles: ArchivedFileRow[] }>(`/clients/${id}`) })
}
/** PRD-020 §3.14: the "Archived clients" node at the bottom of the tree; a separate read since the tree function lists live clients only. */
export interface ArchivedClientRow { id: string, name: string, structure: 'single' | 'group', archived_at: string, file_count: number, archived_by: string | null }
export function useArchivedClients(enabled = true) {
  return useQuery({ queryKey: ['clients', 'archived'] as const, queryFn: async () => (await api<{ clients: ArchivedClientRow[] }>('/clients/archived')).clients, enabled, staleTime: 60_000 })
}
export function useFile(id: string) {
  return useQuery({ queryKey: keys.file(id), queryFn: async () => (await api<{ file: FileDetail }>(`/files/${id}`)).file })
}
export function useTeam() {
  return useQuery({ queryKey: keys.team, queryFn: () => api<{ seats: TeamSeat[], invites: TeamInvite[] }>('/team') })
}

export interface TeamWorkRow { principalId: string, role: Role, displayName: string, open: number, waiting: number, overdue: number, doneThisPeriod: number }
/**
 * PRD-018 v0.4 §3.19 / PRD-017 v0.19 §6.5: the Team → Work tab and Home's Team block, one row per person the
 * caller manages with the same four counts as that person's own My work summary line. CFO and Controller only;
 * the API refuses (409 `team.work_cfo_or_controller`) for every other role, so the hook is only ever mounted
 * behind `capabilities.viewingControl`.
 */
export function useTeamWork(period: string, opts: { enabled?: boolean } = {}) {
  return useQuery({ enabled: opts.enabled ?? true, queryKey: keys.teamWork(period), queryFn: () => api<{ people: TeamWorkRow[], period: string }>(`/team/work?period=${period}`) })
}

// ---------------------------------------------------------------------------------------------
// PRD-017 core: checklist, template, item detail, notifications
// ---------------------------------------------------------------------------------------------

export interface ChecklistItem {
  id: string
  parent_id: string | null
  is_verification: boolean
  template_row_id: string | null
  kind: string
  /** ADR 0030 (QA-32). */
  work_class: 'close' | 'recurring' | 'task'
  title: string
  status: string
  status_detail: Record<string, unknown>
  assignee_principal_id: string
  assignee_name: string
  assignee_kind: 'person' | 'agent'
  /** Tasks review 2026-09-20: the catalog skill bound to the row (template row `skill_id` or a skill-run task's), if any. */
  skill: { id: string, name: string } | null
  /** Whether that skill can run on this file now (the launcher's own Definitions check); null on group rows. */
  run_readiness: RunReadiness | null
  waiting_on_principal_id: string | null
  due_on: string | null
  blocked_reason: string | null
  blocked_since: string | null
  blocked: BlockedIndicator | null
  carried_from_item_id: string | null
  payload: { body?: string, carried_from?: string, subtask_progress?: { done: number, total: number }, file_override?: boolean } & Record<string, unknown>
  version: number
  updated_at: string
  position: number | null
  open_deps: number
  comment_count: number
  /** PRD-017 v0.19 (Seb UX review note 33, decision 9): the most recent job that worked this row, if any — cross-links both ways (the job's step names this row as its "where"). */
  job: { id: string, state: string } | null
}
/** `ready` = bound skill + Definitions complete · `needs_definitions` = bound, blocked on Definitions · `no_skill` = nothing bound. */
export type RunReadiness = 'ready' | 'needs_definitions' | 'no_skill' | 'no_permissions'
export interface Checklist {
  file: { id: string, clusterId: string, name: string }
  period: { id: string | null, label: string, startsOn?: string, endsOn?: string, status: string, closeTargetOn?: string | null }
  instance: { id: string, percentComplete: number, templateVersion: number | null } | null
  template: { id: string, version: number, name: string } | null
  instantiated: number
  items: ChecklistItem[]
}
export function useChecklist(fileId: string, period: string) {
  return useQuery({ queryKey: keys.checklist(fileId, period), queryFn: () => api<Checklist>(`/files/${fileId}/checklist?period=${period}`), enabled: Boolean(fileId && period) })
}

/**
 * ADR 0033: the client's group close for a month: the group rows (blocked while any member file's checklist is
 * open), the member files' progress, the gate and the group percent (equal weight per file plus the group rows).
 * The read opens the month on every member file the reader may open and stamps the group rows (idempotent).
 */
export interface GroupClose {
  cluster: { id: string, name: string, structure: 'single' | 'group' }
  period: { label: string, startsOn: string, endsOn: string, status: 'open' | 'closed' | 'upcoming' }
  instance: { id: string, percentComplete: number, templateVersion: number | null } | null
  gate: { open: boolean, waitingCount: number, waitingOn: Array<{ fileId: string, name: string }> }
  files: Array<{ id: string, name: string, books: 'qbo' | 'none', hasInstance: boolean, done: boolean, openItems: number, rowsTotal: number, rowsDone: number, percent: number | null, closeTargetOn: string | null }>
  /** Member files the reader may not see: counted in the gate, never named. */
  hiddenFiles: number
  items: ChecklistItem[]
}
export function useGroupClose(clusterId: string, period: string) {
  return useQuery({ queryKey: keys.groupClose(clusterId, period), queryFn: () => api<GroupClose>(`/clients/${clusterId}/group-close?period=${period}`), enabled: Boolean(clusterId && period) })
}
/** Home's one read for every card (never one per client): group percent and gate per cluster that has a cluster instance for the month. */
export interface GroupCloseSummary { clusterId: string, percentComplete: number, groupItems: { total: number, done: number, blocked: number }, waitingOnFiles: number }
export function useGroupCloseAll(period: string) {
  return useQuery({ queryKey: keys.groupCloseAll(period), queryFn: async () => (await api<{ clusters: GroupCloseSummary[] }>(`/group-close?period=${period}`)).clusters, enabled: Boolean(period), refetchInterval: 60_000 })
}
/** QA-32: the file Overview's Recurring block: today's items and open ones from earlier days; the read stamps today when the tick has not. */
export interface Recurring {
  file: { id: string, clusterId: string, name: string }
  today: string
  stamped: number
  template: { id: string, version: number, name: string, recurringRows: number } | null
  items: ChecklistItem[]
}
export function useFileRecurring(fileId: string) {
  return useQuery({ queryKey: keys.recurring(fileId), queryFn: () => api<Recurring>(`/files/${fileId}/recurring`), enabled: Boolean(fileId) })
}

export type FileRole = 'operating' | 'holding' | 'management' | 'other'
/** PRD-017 §3.7: four close cadences (period-bound) and, since QA-32 / ADR 0030, three recurring ones (day-bound). */
export type Cadence = 'monthly' | 'quarterly' | 'annually' | 'once' | 'daily' | 'weekly' | 'biweekly'
/** QA-31 / ADR 0029: how the row's resolved dependency set was chosen (none | the row above | a named set). */
export type DependsMode = 'none' | 'previous' | 'explicit'
export interface TemplateRow {
  id: string
  position: number
  title: string
  cadence: Cadence
  /** QA-32: Mon..Sun bitmask (bit 0 = Monday) on a recurring row; 0 on a close row. */
  weekdayMask: number
  dueOffsetDays: number
  defaultAssignee: string
  dependsOnRowIds: string[]
  dependsMode: DependsMode
  appliesTo: 'group' | 'all_files' | 'files_by_role'
  appliesToRoles: FileRole[]
  /** PRD-017 §3.3 "body": how the row's work is done, snapshotted onto each stamped item (`payload.body`); the drawer's instruction line. */
  body: string | null
  /** SI-23: the catalog skill this row's items run under; null = a person does it. */
  skillId: string | null
  /** Migration 0060: the Logic doc the row follows, `<slug>` or `<slug>#<rule_id>`. */
  logicRef: string | null
  inherited: boolean
}
/** ADR 0004 layer 2: a client's change to one firm row (hide / reassign / due_offset) or a client-only row (add). */
export interface TemplateOverride { id: string, template_row_id: string | null, op: 'hide' | 'add' | 'reassign' | 'due_offset', payload: Record<string, unknown>, created_at: string }
export interface Template {
  id: string
  /** Stable identity across versions (ADR 0004 amended 2026-09-13): the first version's id. */
  lineageId: string
  version: number
  name: string
  /** Firm templates only: the one clients with no explicit choice inherit. */
  isDefault: boolean
  /** account = the firm template; cluster = this client's own (detached) template. */
  scope: 'account' | 'cluster'
  /** True when the rows are the firm's and the client changes them through overrides only (ADR 0004). */
  inherited: boolean
  rows: TemplateRow[]
  overrides: TemplateOverride[]
  versions: Array<{ version: number, published_at: string | null, published_by: string | null }>
}
/** One row of Settings → Close templates (`GET /firm/templates`). */
export interface FirmTemplateSummary { id: string, lineageId: string, name: string, version: number, isDefault: boolean, rows: number, publishedAt: string | null, publishedBy: string | null, inheritingClusters: number }
export interface FirmTemplatesAnswer {
  templates: FirmTemplateSummary[]
  defaultLineageId: string | null
  clusters: Array<{ id: string, name: string, detached: boolean, firmTemplateId: string | null, inheritsLineageId: string | null }>
}
/** `GET /firm/templates/:lineageId`: the template, who inherits it, and how many clients override each row. */
export interface FirmTemplateAnswer { template: Template, clusters: Array<{ id: string, name: string, explicit: boolean }>, overriddenOn: Record<string, number> }
/** The picker entries a client's template tab offers (ADR 0004 amended). */
export interface FirmTemplateOption { lineageId: string, name: string, version: number, isDefault: boolean }
export interface ClientTemplateAnswer { template: Template | null, firmTemplateExists: boolean, firmTemplates: FirmTemplateOption[], /** The client's explicit choice; null = the firm default. */ firmTemplateId: string | null, /** QA-20: the distinct file.role values across this client's files, for the "not applicable" tag on a by-role row. */ fileRoles: FileRole[] }
/** `PUT /clients/:id/template` answer: the new version plus whether the firm's open month already has files stamped from an older one (PRD-017 edge case 10). */
export interface PublishResult { id: string, version: number, rows: number, openPeriod: { period: string, filesStamped: number } }
export interface ApplyResult { period: string, version: number | null, files: number, added: number }
export function useTemplate(clusterId: string) {
  return useQuery({ queryKey: keys.template(clusterId), queryFn: () => api<ClientTemplateAnswer>(`/clients/${clusterId}/template`), enabled: Boolean(clusterId) })
}
export function useFirmTemplates() {
  return useQuery({ queryKey: keys.firmTemplates, queryFn: () => api<FirmTemplatesAnswer>('/firm/templates') })
}
export function useFirmTemplate(lineageId: string) {
  return useQuery({ queryKey: keys.firmTemplate(lineageId), queryFn: () => api<FirmTemplateAnswer>(`/firm/templates/${lineageId}`), enabled: Boolean(lineageId) })
}
/** `POST /firm/templates` and `PUT /firm/templates/:lineageId` answer (ADR 0004 amended). */
export interface FirmPublishResult extends PublishResult { lineageId: string, isDefault: boolean, inheritingClusters: number, overridesCarried: number, overridesRetired?: number }
/** `PUT /clients/:id/firm-template` answer (PRD-017 edge case 56). */
export interface SelectFirmTemplateResult { firmTemplateId: string | null, template: { lineageId: string, name: string, version: number }, overridesCarried: number, overridesRetired: number, changed: boolean }

export interface ActivityEvent {
  id: string
  seq: number
  event_type: string
  actor_kind: 'person' | 'agent' | 'rule'
  actor_principal_id: string | null
  actor_name: string | null
  rule_name: string | null
  field_changes: Array<{ field: string, old: unknown, new: unknown }>
  comment_body: string | null
  mentions: string[]
  occurred_at: string
}
/** Per-item capabilities, computed by the API from the same authorize() its mutations run (audit P1-4). */
export interface ItemCapabilities {
  complete: boolean
  transition: boolean
  defer: boolean
  reopen: boolean
  takeOver: boolean
  reassign: 'any' | 'agent_or_self' | 'none'
  dependency: boolean
  override: boolean
  subtask: boolean
  comment: boolean
  due: boolean
  /** QA-29: add or remove a link on the item. */
  link: boolean
  /** ADR 0034: attach or remove a file; false when the instance has no object store. */
  attach: boolean
  /** PRD-025 §3.6 "Who may propose": raise a proposed adjustment on this item's file (CFO, Controller in scope, Tax on a granted file). */
  propose: boolean
  /** PRD-023 §3.3: raise a request to a client contact from this item. */
  requestFromClient: boolean
}
/** PRD-017 §3.14 (ADR 0034): one attachment; `sha256` and previews only once `scanStatus` is clean. */
export interface Attachment {
  id: string
  workItemId: string | null
  fileId: string | null
  clientId: string
  periodId: string | null
  name: string
  mimeType: string
  sizeBytes: number
  sha256: string | null
  uploadedBy: { principalId: string, name: string | null }
  uploadedAt: string
  scanStatus: 'pending' | 'clean' | 'failed'
  scanReason: string | null
  scannedAt: string | null
}
/** The file Close tab's Attachments list row: the attachment plus the item it sits on and its month. */
export interface FileAttachment extends Attachment {
  item: { id: string, title: string | null, workClass: string | null } | null
  period: string
}
/** QA-29 (PRD-017 §3.14 v0.9): one link on a work item; removal is soft and the list holds the live ones only. */
export interface WorkItemLink { id: string, url: string, label: string | null, added_by_principal_id: string, added_by_name: string | null, created_at: string }
export interface ItemDetail {
  workItem: {
    id: string
    title: string
    status: string
    kind: string
    work_class: 'close' | 'recurring' | 'task'
    cluster_id: string
    file_id: string | null
    period_id: string | null
    /** YYYY-MM, null for group-level and unattached items. */
    period: string | null
    parent_id: string | null
    /** The template row this item was stamped from; null for hand-made items. */
    template_row_id: string | null
    is_verification: boolean
    due_on: string | null
    assignee_principal_id: string
    assigneeKind: 'person' | 'agent'
    waiting_on_principal_id: string | null
    blocked_reason: string | null
    blocked_since: string | null
    blocked: BlockedIndicator | null
    status_detail: Record<string, unknown>
    payload: Record<string, unknown>
    carried_from_item_id: string | null
    version: number
    created_at: string
    periodStatus: string | null
    subtasksOpen: boolean
    fileName: string | null
    /** Tasks review 2026-09-20: the bound catalog skill and whether it can run here; the drawer's primary action derives from them. */
    skill: { id: string, name: string } | null
    runReadiness: RunReadiness | null
    /** Tasks review 2026-09-20: how the work is done (the stamped `payload.body`, else the template row's current text) and the Logic doc the row follows. */
    instruction: string | null
    logicRef: string | null
    /** PRD-025 v0.3.9: set on the rule's "Set what the agent may do" item; the drawer links to the file's Agent → Permissions section. */
    permissionsHref?: string | null
  }
  events: ActivityEvent[]
  children: Array<{ id: string, title: string, status: string, kind: string, is_verification: boolean, assignee_principal_id: string, assignee_name: string, assignee_kind: string, due_on: string | null, blocked_reason: string | null, status_detail?: Record<string, unknown> }>
  dependencies: Array<{ id: string, blocking_item_id: string, origin: string, satisfied_at: string | null, overridden_by_event_id: string | null, blocking_title: string, blocking_status: string }>
  dependents: Array<{ id: string, blocked_item_id: string, blocked_title: string, blocked_status: string, releases_on_done: boolean }>
  /**
   * ADR 0031: the open downstream chain a deferral would carry (breadth-first; `depth` 1 = waits on this item
   * directly). Empty for done, recurring, client-level and sub-task items. The defer dialog lists it with every
   * carriable node ticked; a Member sees it as the "ask your controller" line.
   */
  deferChain: Array<{ id: string, title: string, status: string, fileId: string | null, depth: number, viaItemId: string, carriable: boolean, assigneeName: string | null, fileName: string | null }>
  links: WorkItemLink[]
  attachments: Attachment[]
  attachmentsEnabled: boolean
  parent: { id: string, title: string } | null
  fileAgent: { principalId: string, name: string } | null
  can: ItemCapabilities
  principals: Record<string, { name: string, kind: string }>
  /** PRD-023: set when this item is itself a client request (kind request, payload.request_target client). */
  clientRequest: { id: string } | null
  /** PRD-023: requests raised from this item; the item is blocked until each is accepted. */
  raisedRequests: Array<{ id: string, title: string, status: string, contact_name: string }>
}
export function useItem(id: string) {
  return useQuery({
    queryKey: keys.item(id),
    queryFn: () => api<ItemDetail>(`/work-items/${id}`),
    enabled: Boolean(id),
    // ADR 0034: while any attachment is still being scanned, poll so the chip flips to Clean without a reload.
    refetchInterval: q => (q.state.data?.attachments.some(a => a.scanStatus === 'pending') ? 2_000 : false),
  })
}
export function useFileAttachments(fileId: string, period: string, itemId: string | null = null) {
  const qs = new URLSearchParams({ period })
  if (itemId)
    qs.set('item', itemId)
  return useQuery({
    queryKey: keys.fileAttachments(fileId, period, itemId),
    queryFn: () => api<{ attachments: FileAttachment[], enabled: boolean }>(`/files/${fileId}/attachments?${qs}`),
    enabled: Boolean(fileId && period),
    refetchInterval: q => (q.state.data?.attachments.some(a => a.scanStatus === 'pending') ? 2_000 : false),
  })
}

export interface Notification {
  id: string
  type: 'agent_needs_input' | 'approval_requested' | 'assigned_to_you' | 'mention_or_reply' | 'needs_your_attention'
  /** Null for a file-keyed `needs_your_attention` row (PRD-020 §3.9 connection notices; migration 0045). */
  work_item_id: string | null
  raised_at: string
  read_at: string | null
  resolved_at: string | null
  title: string
  /** Null for a file-keyed row. */
  status: string | null
  /** File-keyed rows only: kind, body, reason, days to the cliff, why this recipient, why it resolved. */
  detail: { kind: 'needs_reconnect' | 'expiry_80' | 'expiry_95', body: string, reason?: string, expires_in_days?: number, recipient_basis: 'manager' | 'lead' | 'cfo', resolved_reason?: string } | null
  cluster_id: string
  file_id: string | null
  cluster_name: string
  file_name: string | null
  actor_name: string | null
  comment_body: string | null
}
export function useNotifications(all: boolean) {
  return useQuery({ queryKey: [...keys.notifications, all] as const, queryFn: () => api<{ notifications: Notification[], unread: number }>(`/notifications${all ? '?all=1' : ''}`), refetchInterval: 60_000 })
}

// ---------------------------------------------------------------------------------------------
// PRD-020: connection, accounts, contacts, pending connection
// ---------------------------------------------------------------------------------------------

export type ConnectionState = 'not_connected' | 'syncing' | 'connected' | 'sync_failed' | 'needs_reconnect' | 'disconnected'
export interface Connection {
  fileId: string
  state: ConnectionState
  realmMasked: string | null
  environment: 'production' | 'sandbox' | null
  connectedAt: string | null
  connectedBy: string | null
  disconnectedAt: string | null
  disconnectedBy: string | null
  lastSyncAt: string | null
  lastSyncStatus: 'succeeded' | 'failed' | null
  lastSyncReason: string | null
  lastRefreshAt: string | null
  refreshExpiresAt: string | null
  expiresInDays: number | null
  connectEnabled: boolean
  readSurface: string[]
}
export interface SyncRun { id: string, trigger: string, started_at: string, finished_at: string | null, status: 'running' | 'succeeded' | 'failed', reason: string | null, surfaces: Record<string, unknown>, period: string | null }
export function useConnection(fileId: string, opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: [...keys.file(fileId), 'connection'] as const,
    queryFn: () => api<{ connection: Connection, runs: SyncRun[] }>(`/files/${fileId}/connection`),
    refetchInterval: q => (opts.poll !== false && q.state.data?.connection.state === 'syncing') ? 3000 : false,
  })
}
/** One chart-of-accounts row as the API maps it. `parentId` / `subAccount` carry QBO's hierarchy (QA-15); both balances are QBO's strings. */
export interface QboAccount { id: string, name: string, fullyQualifiedName: string | null, number: string | null, type: string, subType: string | null, classification: string | null, active: boolean, subAccount: boolean, parentId: string | null, currentBalance: string | null, balanceWithSubAccounts: string | null, currency: string | null }
export function useAccounts(fileId: string) {
  return useQuery({ queryKey: [...keys.file(fileId), 'accounts'] as const, queryFn: () => api<{ accounts: QboAccount[] | null, readAt?: string }>(`/files/${fileId}/accounts`) })
}
/**
 * PRD-020 §3.7 Reports tab: the trial balance snapshot for a month and basis. `pending: true` (a 202) means the
 * API enqueued a one-read sync for that period (§6.3 period change); the tab polls until the snapshot lands.
 * `pending: false` with no report means nothing was read and nothing can be (the file is not connected).
 */
export type TrialBalanceBasis = 'Accrual' | 'Cash'
export interface TrialBalanceResponse { trialBalance: unknown | null, readAt?: string, period: string, periodId?: string, basis: TrialBalanceBasis, pending?: boolean }
export function useTrialBalance(fileId: string, period: string, basis: TrialBalanceBasis, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...keys.file(fileId), 'trial-balance', period, basis] as const,
    queryFn: () => api<TrialBalanceResponse>(`/files/${fileId}/trial-balance?period=${encodeURIComponent(period)}&basis=${basis}`),
    enabled: opts.enabled !== false && Boolean(fileId && period),
    refetchInterval: q => (q.state.data?.pending ? 3000 : false),
  })
}
/** QA-22: the client Accounts tab. One row per account across the client's files; `cells` keyed by file id. QA-22b: `renamed` = matched by number under another name. */
export interface CoaCompareCell { present: true, id: string, name: string, fullyQualifiedName: string, number: string | null, type: string, subType: string | null, active: boolean, parentDiffers: boolean, renamed: boolean }
export interface CoaCompareRow { key: string, name: string, fullyQualifiedName: string, number: string | null, type: string, subType: string | null, active: boolean, parentKey: string | null, cells: Record<string, CoaCompareCell | { present: false }>, missingFrom: string[], flags: { typeMismatch: boolean, subTypeMismatch: boolean, numberMismatch: boolean, parentMismatch: boolean, renamed: boolean }, status: 'aligned' | 'missing' | 'mismatched' }
export interface CoaCompareFile { id: string, displayName: string, connectionState: ConnectionState, synced: boolean, readAt: string | null, stale: boolean, accountCount: number | null }
export interface CoaCompare { client: { id: string, name: string, structure: 'single' | 'group' }, files: CoaCompareFile[], compared: boolean, rows: CoaCompareRow[], summary: { accounts: number, aligned: number, missingSomewhere: number, mismatched: number }, duplicates: Record<string, string[]>, readOnly: true }
/** 204 (fewer than two files visible) arrives as an empty body; the tab reads `null` as "nothing to compare". */
export function useCoaCompare(clientId: string) {
  return useQuery({ queryKey: [...keys.client(clientId), 'coa-compare'] as const, queryFn: async () => {
    const body = await api<Partial<CoaCompare>>(`/clients/${clientId}/coa-compare`)
    return body.files ? body as CoaCompare : null
  } })
}
export interface Contact { id: string, client_id: string, name: string, organisation: string | null, role: string, email: string | null, phone: string | null, note: string | null, karbon_origin: unknown, archived_at: string | null }
export function useContacts(clientId: string) {
  return useQuery({ queryKey: [...keys.client(clientId), 'contacts'] as const, queryFn: async () => (await api<{ contacts: Contact[] }>(`/clients/${clientId}/contacts`)).contacts })
}
export interface PendingConnection { clientId: string, clientName: string, clientJurisdictions: Jurisdiction[], leadSeatId: string | null, realmMasked: string | null, environment: string, legalName: string | null, companyName: string | null, fiscalYearStartMonth: number | null, country: string | null, city: string | null, region: string | null, address: Address | null, entityTypes: string[], expiresAt: string }
export function usePendingConnection(nonce: string) {
  return useQuery({ queryKey: ['connect', 'pending', nonce] as const, queryFn: async () => (await api<{ pending: PendingConnection }>(`/connect/qbo/pending?nonce=${encodeURIComponent(nonce)}`)).pending, retry: false, enabled: nonce.length > 0 })
}
export function useConnectStatus() {
  return useQuery({ queryKey: ['connect', 'status'] as const, queryFn: () => api<{ enabled: boolean }>('/connect/qbo/status'), staleTime: 5 * 60_000 })
}
/** PRD-020 §3.19 Reconnect all (CFO): every QuickBooks connection in the account with its state. */
export interface ConnectionRow { fileId: string, clientId: string, clientName: string, fileName: string, legalName: string | null, state: ConnectionState, realmMasked: string | null, environment: 'production' | 'sandbox' | null, connectedAt: string | null, disconnectedAt: string | null, lastSyncAt: string | null, lastSyncStatus: string | null, lastSyncReason: string | null, lastRefreshAt: string | null, expiresInDays: number | null, attentionOpen: boolean }
export function useConnections(opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: ['connections'] as const,
    queryFn: () => api<{ connectEnabled: boolean, environment: string | null, connections: ConnectionRow[] }>('/connections'),
    refetchInterval: q => (opts.poll !== false && q.state.data?.connections.some(c => c.state === 'syncing')) ? 4000 : false,
  })
}

// ---------------------------------------------------------------------------------------------
// PRD-025 slice 1: the skill thread behind a work item and its Review cards
// ---------------------------------------------------------------------------------------------

export type PostingState = 'staged' | 'approved' | 'synced_to_qbo' | 'rejected' | 'reversed'
export type ReviewWriteType = 'reclass' | 'adjustment' | 'categorisation' | 'accrual' | 'reversal'
export interface ProposedLine { account_token: string, amount: string, memo?: string }
export interface ReviewCitation { kind: 'logic' | 'source_row' | 'snapshot' | 'attachment' | 'answer', ref: string, quote?: string }
export interface ReviewItem {
  id: string
  thread_id: string
  attempt_id: string
  work_item_id: string
  file_id: string
  period_id: string | null
  periodLabel: string | null
  write_type: ReviewWriteType
  proposed: { v: 1, target: { account_tokens: string[], row_ids: string[] }, before: { lines: ProposedLine[] }, after: { lines: ProposedLine[] }, memo: string, rationale: string }
  edited: { v: 1, by_principal_id: string, at: string, before: { lines: ProposedLine[] }, after: { lines: ProposedLine[] } } | null
  evidence: { v: 1, citations: ReviewCitation[] }
  posting_state: PostingState
  proposed_by_principal_id: string
  originator_seat_id: string | null
  originatorPrincipalId: string | null
  decided_by_principal_id: string | null
  decided_at: string | null
  approved_with_edit: boolean
  reject_reason: string | null
  conflicts_with_review_item_id: string | null
  qbo_txn_id: string | null
  /** Slice 2a: what QuickBooks assigned (`JournalEntry` | `Purchase` | `Deposit`), the SyncToken, the `G…` DocNumber marker for an entry Gallop created, and when. */
  qbo_txn_type: string | null
  qbo_sync_token: string | null
  qbo_doc_number: string | null
  synced_at: string | null
  reversal_of_review_item_id: string | null
  sync_error: { v: 1, code: string, message: string } | null
  /** UX audit 2026-09-19 P1-1 (migration 0051): the rule under which the decider decided their own card, else null. */
  self_decision_rule: 'cfo_sole_approver' | null
  /** P1-1: who the `review` child waits on (the named approver) and its status; null once decided. */
  waitingOnPrincipalId: string | null
  childStatus: string | null
  created_at: string
  can: { propose: boolean, approve: boolean, edit: boolean, reject: boolean, retry: boolean, reverse: boolean, withdraw: boolean, approveReason: string | null, soleApprover: boolean }
}
/** P1-2: an account token re-hydrated for the reader from the file's latest accounts snapshot. */
export interface HydratedAccount { token: string, id: string, number: string | null, name: string, fullyQualifiedName: string | null, active: boolean }
export interface ThreadEvent { id: string, seq: number, kind: string, attempt_no: number | null, actor_principal_id: string | null, actor_kind: 'person' | 'agent' | 'rule' | 'operator', occurred_at: string, payload: Record<string, unknown>, related_review_item_id: string | null }
export interface ThreadMessage { id: string, seq: number, kind: string, attempt_id: string, author_principal_id: string, body: Record<string, unknown>, work_item_id: string | null, created_at: string, /** Contract §1: per-card capabilities (`answer` on a question card, `accept` on a result card). */ can?: { answer?: boolean, accept?: boolean } }
/**
 * PRD-025 §3.4 question card body (`thread_message.kind = question_card`, §6.2): the kind, the prompt, its options or
 * expected value type, the step that needs it, who is asked, the wait limit, and the answer once one is recorded.
 */
export type QuestionKind = 'choose_one' | 'confirm' | 'provide_value' | 'provide_document' | 'free_text'
/** Contract §1: `value` is the option's identity; `id` was this branch's first name and is still read. */
export interface QuestionOption { value?: string, id?: string, label: string, recommended?: boolean }
export interface QuestionBody {
  v: 1
  question_kind: QuestionKind
  prompt: string
  options?: QuestionOption[]
  value_type?: 'text' | 'number' | 'amount' | 'decimal' | 'date' | null
  doc_types?: string[]
  reason_step?: string
  asked_of_principal_id?: string
  wait_until?: string
  answer?: { event_id: string, by_principal_id: string, value: unknown, at?: string }
}
/** `thread_attempt` as the API projects it for a customer seat (§6.6: no digest, credential, container or tool ids). */
export interface ThreadAttempt {
  id: string
  attempt_no: number
  state: string
  started_at: string | null
  ended_at: string | null
  model_calls: string
  model_input_tokens: string
  model_output_tokens: string
  model_cache_tokens?: string
  runtime_ms?: string | null
  /** `usd_micros`, populated once a credits rate exists (§8 Q6); null = tokens only. */
  usd_micros?: string | null
  /** Operator seats only (`OPERATOR_ONLY_KEYS` strips it for partners): the digest the spawn approval binds to (§3.2). */
  manifest_digest?: string | null
  manifest?: { v?: number, runtime_model_profile?: string, skill?: { id?: string, version_id?: string, name?: string }, logic?: Array<{ doc_id: string, version_id: string, name?: string }>, budgets?: { runtime_ms?: number, model_tokens?: number, usd_micros?: number } } & Record<string, unknown>
  grounded_in?: { v?: number, logic?: Array<{ doc_id: string, version_id: string, name?: string }>, source_rows?: Array<{ kind: string, count: number, ids?: string[] }>, snapshots?: Array<{ snapshot_id: string, taken_at: string }>, request_log_ids?: string[], attachments?: string[], answers?: string[] } & Record<string, unknown>
}
export interface ThreadDetail {
  thread: { id: string, work_item_id: string, skill_id: string | null, skill_version_id?: string | null, agent_principal_id: string, file_id: string | null, cluster_id?: string | null, period_id?: string | null, state: string, state_reason: string | null, current_attempt_no: number, opened_by_principal_id: string, opened_at: string, closed_at: string | null } | null
  parent: { id: string, title: string, status: string } | null
  attempts: ThreadAttempt[]
  messages: ThreadMessage[]
  events: ThreadEvent[]
  reviewItems: ReviewItem[]
  /** P1-2: token → account, for every token on the cards; a token missing here is not in the file's latest accounts read. */
  accounts: Record<string, HydratedAccount>
  can: { approveAll: boolean, windowOpen: boolean, /** Whether this seat may answer an open question card (the contract puts it on the message; this is the thread-level fallback). */ answer?: boolean }
  principals: Record<string, { name: string, kind: string }>
  /** Contract §1 (workstream A): the header projection; absent from the slice 1 API, in which case the web derives the header from `attempts`. */
  header?: ThreadHeaderProjection
}
export interface ThreadHeaderProjection {
  skill: { id: string, name: string, version?: string | null } | null
  agent: { principalId: string, name: string, tier?: string }
  state: string
  stateReason: string | null
  provider: string | null
  modelProfile: string | null
  tokens: { input: number, output: number, cache: number, calls: number }
  costEstimate: { usdMicros: number, basis: 'estimate' | 'rate', note?: string } | null
  budgets?: { runtimeMs?: number, modelTokens?: number }
  groundedIn?: { logic?: Array<{ docId: string, versionId: string, name?: string }>, sourceRows?: Array<{ kind: string, count: number, ids?: string[] }>, snapshots?: Array<{ snapshotId: string, takenAt: string }>, requestLogIds?: string[], attachments?: string[], answers?: string[] }
  provisioning?: { approvedAt: string | null, approvedBy: { principalId: string, name: string } | null, sources: Array<{ kind: string, name: string, version: string }>, tools: Array<{ id: string, label: string }> }
  /** ADR 0052: "Effective skill · base vX + N overrides", linking to the diff view; null for attempts assembled before it. */
  effectiveSkill?: { baseVersion: string, parameterCount: number, overrideCount: number, attemptNo: number, href: string } | null
  can?: { cancel?: boolean, retry?: boolean, acceptResult?: boolean }
}

/** The thread a work item hangs off (the parent) or raised (a `review` child); `thread: null` when none. */
export function useItemThread(itemId: string) {
  return useQuery({
    queryKey: keys.itemThread(itemId),
    queryFn: () => api<ThreadDetail | { thread: null }>(`/work-items/${itemId}/thread`),
    enabled: Boolean(itemId),
    // A card reads `approved` until the platform's write lands after commit; poll while one is in that state.
    refetchInterval: q => ((q.state.data as ThreadDetail | undefined)?.reviewItems?.some(r => r.posting_state === 'approved' && !r.sync_error) ? 2_000 : false),
  })
}

/** `GET /threads/:id` (§6.4): the same payload keyed by the thread, for the Threads page's open pane. */
export function useThread(threadId: string) {
  return useQuery({
    queryKey: keys.thread(threadId),
    queryFn: () => api<ThreadDetail>(`/threads/${threadId}`),
    enabled: Boolean(threadId),
    refetchInterval: q => ((q.state.data?.thread && (q.state.data.thread.state === 'running' || q.state.data.reviewItems.some(r => r.posting_state === 'approved' && !r.sync_error))) ? 3_000 : false),
  })
}

// ---------------------------------------------------------------------------------------------
// PRD-025 slice 2b: the threads list (person-facing "Agent jobs" since PRD-016 v2.6.2), the file agent's write types, the skill library and the launcher
// ---------------------------------------------------------------------------------------------

export type ThreadStateGroup = 'waiting' | 'running' | 'blocked' | 'done'
export interface ThreadFilters { period?: string | null, state?: ThreadStateGroup | 'all' | null, fileId?: string | null, clientId?: string | null, waiting?: 'me' | null }
/**
 * One row of `GET /threads`: `docs/api/prd025-slice2b-contracts.md` §1 `ThreadSummary` (workstream A, frozen 2026-09-19
 * 11:40) plus the optional extras this branch's API adds for the list (`group`, `waitingOn`, `lastEvent`, `modelProfile`).
 * The web derives what it can when an extra is absent, so either API answers the page.
 */
export interface ThreadListRow {
  id: string
  state: string
  stateReason: string | null
  skillId: string | null
  skillName: string
  skillVersion?: string | null
  agent: { principalId: string, name: string, tier?: 'file' | 'intercompany' | 'tax' }
  file: { id: string, name: string } | null
  cluster: { id: string, name: string }
  period: { id: string | null, label: string } | null
  workItem: { id: string, title: string, status: string }
  currentAttemptNo: number
  openCards: { questions: number, reviews: number }
  openedAt: string
  updatedAt?: string
  closedAt: string | null
  // Extras
  group?: ThreadStateGroup
  waitingOn?: { principalId: string, name: string, kind: 'question' | 'review' } | null
  lastEvent?: { kind: string, at: string } | null
  modelProfile?: string | null
}
/** PRD-016 §7.6 groups the seven states into four for the list; the API sends `group` and this derives it when absent. */
export function threadGroup(t: Pick<ThreadListRow, 'state' | 'group'>): ThreadStateGroup {
  if (t.group)
    return t.group
  switch (t.state) {
    case 'waiting_on_human': return 'waiting'
    case 'running': return 'running'
    case 'budget_exhausted': case 'timed_out': case 'failed': return 'blocked'
    default: return 'done'
  }
}
export interface ThreadList { threads: ThreadListRow[], nextCursor?: string | null, counts?: Record<ThreadStateGroup | 'all', number>, waitingOnMe?: number, period?: string, state?: string, filterNote?: string }
export function useThreads(filters: ThreadFilters, opts: { enabled?: boolean } = {}) {
  // Contract §1 names: `period` absent = every period; `state` repeatable (a group name is accepted by this branch's API); `fileId` / `clusterId`.
  const qs = new URLSearchParams()
  if (filters.period && filters.period !== 'all')
    qs.set('period', filters.period)
  if (filters.state && filters.state !== 'all')
    qs.set('state', filters.state)
  if (filters.fileId)
    qs.set('fileId', filters.fileId)
  if (filters.clientId)
    qs.set('clusterId', filters.clientId)
  if (filters.waiting)
    qs.set('waiting', filters.waiting)
  return useQuery({
    queryKey: keys.threads(filters),
    queryFn: () => api<ThreadList>(`/threads?${qs}`),
    enabled: opts.enabled !== false,
    // A running thread's row changes with every agent message; the list stays live without a reload.
    refetchInterval: q => (q.state.data?.threads.some(t => threadGroup(t) === 'running') ? 5_000 : 60_000),
  })
}

/** `GET /files/:id/agent` (contract §2, PRD-025 §8 Q21): the file agent, its allowlist, the write-type vocabulary and whether this seat may change it. */
export interface AgentWriteTypes {
  fileId?: string
  agent: { principalId: string, name: string, tier?: 'file' | 'intercompany' | 'tax' } | null
  allowlist: { version: number, writeTypes: ReviewWriteType[], skillIds?: string[], connectionIds?: string[] } | null
  writeTypes: { all: ReviewWriteType[], on: ReviewWriteType[] }
  paper?: boolean
  archived?: boolean
  version?: number
  updatedAt?: string | null
  updatedBy?: string | null
  can: { setWriteTypes: boolean, reason?: string | null, stepUp?: 'none' | 'tier1' | 'tier2' | null }
}
export function useAgentWriteTypes(fileId: string) {
  return useQuery({ queryKey: keys.agentWriteTypes(fileId), queryFn: () => api<AgentWriteTypes>(`/files/${fileId}/agent`), enabled: Boolean(fileId) })
}

/**
 * `GET /files/:id/skills` (PRD-017 v0.19 §3.11): the New work item dialog's Skill select for a file's agent. Every
 * catalog skill with `runnable` and the one reason it is not ("Needs permission from a CFO", "Needs Definitions").
 */
export type FileSkillReason = 'not_permitted' | 'needs_definitions' | 'no_permissions_version' | 'unknown_skill' | null
export interface FileSkillOption { id: string, name: string, summary: string | null, runnable: boolean, reason: FileSkillReason, reasonText: string | null }
export interface FileSkills { fileId: string, agent: { principalId: string, name: string } | null, skills: FileSkillOption[] }
export function useFileSkills(fileId: string | null, enabled = true) {
  return useQuery({ queryKey: keys.fileSkills(fileId ?? ''), queryFn: () => api<FileSkills>(`/files/${fileId}/skills`), enabled: Boolean(fileId) && enabled })
}

/**
 * `GET /skills` (contract §4): the firm library (PRD-016 §6.1 skills are firm-wide; §7.6 a `needs_setup` skill is listed
 * disabled with its reason). `state`, `cadence` and `family` are optional: A's first catalog ships one skill from code
 * with none of them, and the launcher treats a skill without a state as runnable.
 */
export interface SkillSummary {
  id: string
  name: string
  version?: string
  summary?: string | null
  agentTier: 'file' | 'intercompany' | 'tax'
  state?: 'proving' | 'proven' | 'needs_setup' | 'off'
  stateReason?: string | null
  cadence?: string | null
  /** PRD-016 §8.4 "Run close check": when any skill declares a family, that button lists the close-check ones only. */
  family?: 'close_check' | 'triage' | 'reconciliation' | 'tax' | 'other'
  tools?: Array<{ id: string, label: string }>
  /**
   * `requires.logic` is the list of Logic doc *types* the frozen contract named (`methodology`, `reference_table`,
   * `procedure`); `requires.logicDocs` is what the bundle resolver pins: the doc slugs, with their type. Links go
   * through the slugs, never the types (UX review 2026-09-21 note 55: `/firm/logic/methodology` is not a page).
   */
  requires?: { connection?: boolean, logic?: string[], logic_docs?: string[], logicDocs?: Array<{ slug: string, type: 'methodology' | 'procedure' | 'reference_table', required: boolean }> }
  writeTypes?: ReviewWriteType[]
}
export function useSkills(opts: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: keys.skills, queryFn: () => api<{ skills: SkillSummary[] }>('/skills'), enabled: opts.enabled !== false, staleTime: 60_000, retry: false })
}

// ---------------------------------------------------------------------------------------------
// PRD-029: notification preferences, devices, held emails
// ---------------------------------------------------------------------------------------------

export type NotificationChannel = 'push' | 'email' | 'slack'
export interface NotificationPreferences {
  channels: { offered: NotificationChannel[], mirror: boolean, inApp: true }
  types: Array<{ type: Notification['type'], mandatory: boolean, push: boolean, email: boolean, slack: boolean, emailMode: 'immediate' | 'digest' }>
  quietHours: { start: string, end: string, enabled: boolean }
  devices: Array<{ id: string, userAgent: string | null, createdAt: string, lastSuccessAt: string | null, revokedAt: string | null, revokeReason: string | null }>
  push: { offered: boolean, publicKey: string | null }
}
export const notificationPrefsKey = ['notification-preferences'] as const
export function useNotificationPreferences() {
  return useQuery({ queryKey: notificationPrefsKey, queryFn: () => api<NotificationPreferences>('/me/notification-preferences') })
}

export interface HeldEmail {
  id: string
  from_address: string
  from_name: string | null
  sender_name: string | null
  subject: string
  received_at: string
  client_id: string | null
  file_id: string | null
  client_name: string | null
  file_name: string | null
  match_basis: 'contact' | 'seat' | 'none' | 'ambiguous'
  match_candidates: Array<{ id: string, name: string | null }>
  state: 'received' | 'held' | 'converted' | 'dismissed' | 'auto_task'
  body_status: 'pending' | 'fetched' | 'unavailable'
  attachment_count: number
  work_item_id: string | null
  dismissed_reason: string | null
  acted_by_name: string | null
  acted_at: string | null
  body_text?: string | null
}
export const heldEmailsKey = (state: 'held' | 'dismissed') => ['held-emails', state] as const
export function useHeldEmails(state: 'held' | 'dismissed' = 'held') {
  return useQuery({ queryKey: heldEmailsKey(state), queryFn: () => api<{ emails: HeldEmail[], count: number }>(`/held-emails?state=${state}`), refetchInterval: 60_000 })
}

// ---------------------------------------------------------------------------------------------
// PRD-023 / PRD-024 slice 1: portal users, client requests, forms (firm side)
// ---------------------------------------------------------------------------------------------
export interface ClientUser { id: string, contactId: string, contactName?: string, principalId: string, email: string, status: 'invited' | 'active' | 'disabled', invitedAt: string, acceptedAt: string | null, disabledAt: string | null, disabledReason: string | null, lastSignInAt: string | null }
export function useClientUsers(clientId: string) {
  return useQuery({ queryKey: [...keys.client(clientId), 'client-users'] as const, queryFn: async () => (await api<{ clientUsers: ClientUser[] }>(`/clients/${clientId}/client-users`)).clientUsers })
}
export interface FormFieldDef { key: string, label: string, help?: string, required?: boolean, writes_to?: string, type: 'short_text' | 'integer' | 'decimal' | 'select' | 'yes_no' | 'date' | 'email' | 'phone' | 'note', options?: Array<{ key: string, label: string }>, max_length?: number, min?: number, max?: number, scale?: number }
export interface ClientRequest {
  id: string
  workItemId: string
  clientId: string
  fileId: string | null
  title: string
  instructions: string
  responseKind: 'upload' | 'confirm' | 'credential' | 'form'
  status: string
  clientState: 'requested' | 'received' | 'accepted' | 'needs_another'
  dueOn: string | null
  contact: { clientUserId: string, name: string, email: string, portalStatus: string }
  raisedBy: { principalId: string, name: string }
  sourceWorkItem: { id: string, title: string | null } | null
  form: { id: string, name: string | null, fields: FormFieldDef[] | null } | null
  receivedAt: string | null
  receivedVia: string | null
  clientNote: string | null
  acceptedAt: string | null
  reopenReason: string | null
  createdAt: string
}
export interface ClientRequestDetail extends ClientRequest {
  attachments: Attachment[]
  attachmentsEnabled: boolean
  submissions: Array<{ id: string, submitted_at: string, values: Record<string, unknown>, review_status: string, accepted_at: string | null, applied_writes: Array<{ key: string, target: string, old: unknown, new: unknown }>, submitter_name: string }>
}
export function useClientRequests(clientId: string) {
  return useQuery({ queryKey: [...keys.client(clientId), 'requests'] as const, queryFn: async () => (await api<{ requests: ClientRequest[] }>(`/clients/${clientId}/requests`)).requests })
}
export function useClientRequest(id: string | null) {
  return useQuery({ queryKey: ['requests', id] as const, queryFn: () => api<ClientRequestDetail>(`/requests/${id}`), enabled: id !== null })
}
export interface FormSummary { id: string, name: string, purpose: string | null, fields: FormFieldDef[], state: 'draft' | 'published' | 'retired', lineage_id: string, published_at: string | null, retired_at: string | null, created_at: string, created_by_name: string, request_count: number, submission_count: number }
export function useForms(state?: 'published' | 'draft' | 'retired') {
  return useQuery({ queryKey: ['forms', state ?? 'all'] as const, queryFn: async () => (await api<{ forms: FormSummary[] }>(`/forms${state ? `?state=${state}` : ''}`)).forms })
}
